import bugsnagClient from "JS/global/bugsnag";
import { BForm } from "JS/components/blain-form";
import { ccexp } from "Src/js/components/blain-form/validation";
import { cyberSourceErrors } from "JS/constants/cybersource.constants";
import { IBillingAddress, ICreditCard } from "JS/types/CyberSource";
import { ICyberSourceDate, ICyberSourceMicroformContext } from "JS/types/Payment";
import { Ref } from "vue";
import { ValidationResult } from "JS/components/blain-form/internal-types";

export class MicroformClientLibraryLoadError extends Error {
	event: Event | string;
	source?: string;
	lineno?: number;
	colno?: number;
	error?: Error
	constructor(errorData: {
		event: Event | string;
		source?: string;
		lineno?: number;
		colno?: number;
		error?: Error
	}) {
		super('Failed to load Flex Microforms clientLibrary');
		Object.assign(this, errorData);
	}
}

export class MicroformCardNumberField {
	microform: () => Microform;
	validate: () => ValidationResult;
	resetValidation: () => void;

	constructor(opt: {
		microform: () => Microform,
		validate: () => ValidationResult,
		resetValidation: () => void
	} ) {
		this.microform = opt.microform;
		this.validate = opt.validate;
		this.resetValidation = opt.resetValidation;
	}
}

export function tryGetMicroformFromBForm(form: Pick<InstanceType<typeof BForm>, 'findChild'>): Microform | null {
	const field = form?.findChild((c): c is MicroformCardNumberField => c instanceof MicroformCardNumberField);
	return field?.microform() ?? null;
}

export async function loadFlexMicroformsClientLibrary(context: ICyberSourceMicroformContext): Promise<void> {
	return new Promise((resolve, reject) => {
		//Adapted from https://developer.cybersource.com/docs/cybs/en-us/digital-accept-flex/developer/all/rest/digital-accept-flex/microform-integ-v2/microform-integ-getting-started-v2.html
		const head = document.getElementsByTagName('head')[0];
		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.async = true;
		script.onload = function() {
			resolve();
		};
		script.onerror = (event: Event | string, source?: string, lineno?: number, colno?: number, error?: Error) => {
			reject(new MicroformClientLibraryLoadError({
				event,
				source,
				lineno,
				colno,
				error,
			}));
		};

		//url extracted from the JWT
		script.src = context.clientLibrary;

		//integrity extracted from the JWT
		if (context.clientLibraryIntegrity) {
			script.integrity = context.clientLibraryIntegrity;
			script.crossOrigin = "anonymous"; //credentials not required
		}

		head.appendChild(script);
	});
}

export function setUpMicroformField(microform: Microform): MicroformField {
	const fieldStyles = {
		'input': {
			'font-size': '1rem',
			'line-height': '1.3125'
		},
		'::placeholder': {
			'color': '#6b74a0'
		},
		//Hover is required do not remove. Flex microform JS bug.
		':hover': {
		},
	};

	return microform.createField('number',
		{
			placeholder: 'Enter card number',
			'aria-label': 'card number',
			'aria-required': true,
			styles: fieldStyles
		}
	);
}

export async function tryGetTransientToken(
	cardInfo: ICreditCard,
	microform: Microform,
	billingAddress: IBillingAddress): Promise<string> {
	if (!isValidMicroform(cardInfo, microform)) {
		return null;
	}

	const options = getCardDate(cardInfo.details.ccExpDate);
	if (cardInfo.details.sameAsShipping) {
		cardInfo.details.billingAddress = billingAddress;
	}

	const timeout = new Promise<string>((resolve) => {
		setTimeout(resolve, 10000, "timeout");
	});

	const tokenize = new Promise<string>(resolve => {
		microform.createToken(options, async function (err, token) {
			if (err) {
				switch (err.reason) {
					case 'CREATE_TOKEN_VALIDATION_PARAMS':
					case 'CREATE_TOKEN_VALIDATION_FIELDS':
						cardInfo.details.inlineValidationError = 'Invalid card format.';
						break;
					case 'CREATE_TOKEN_NO_FIELDS_LOADED':
					case 'CREATE_TOKEN_TIMEOUT':
					case 'CREATE_TOKEN_NO_FIELDS':
					case 'CREATE_TOKEN_VALIDATION_SERVERSIDE':
					case 'CREATE_TOKEN_UNABLE_TO_START':
					default:
						bugsnagClient.notify(err, {
							metaData: {
								info: 'Failed to submit CyberSource flex microform'
							},
						});
						cardInfo.details.inlineValidationError = cyberSourceErrors.DEFAULT_ERROR;
				}
				resolve(null);
			} else {
				resolve(token);
			}
		});
	});

	const result: string = await Promise.race([tokenize, timeout]);
	if (result == "timeout") {
		bugsnagClient.notify("Timeout on cybersource flex microform submission", {
			metaData: {
				location: 'AutomotiveScheduler'
			},
		});
		cardInfo.details.inlineValidationError = cyberSourceErrors.CONNECTION_ERROR_GENERAL;
		return null;
	}

	return result;
}

export function getCardDate(ccExpDate: string): ICyberSourceDate {
	let expMonth = "";
	let expYear = "";
	if (ccExpDate.length > 4 && isValidCardDate(ccExpDate)) {
		const dateArray = ccExpDate.split('/');
		expMonth = dateArray[0];
		expYear = dateArray[1];
		if (expYear.length < 4) {
			    expYear = "20" + expYear;
		}
	}

	return {
		expirationMonth: expMonth,
		expirationYear: expYear
	};
}

export function isValidMicroform(cardInfo: ICreditCard, microform: object): boolean {
	if (!microform) { //sanity check
		return false;
	}

	if (cardInfo.details.inlineValidationError) {
		return false;
	}

	// Compiling MM & YY into optional parameters
	let date = cardInfo.details.ccExpDate;
	date = date.replace('-','/');
	if (date.length < 5 || !isValidCardDate(date)) {
		return false;
	}

	return true;
}

function isValidCardDate(expiryDate: string): boolean {
	const validExpiryDate = ccexp(expiryDate);
	return validExpiryDate === true;
}